import { useQuery } from "@tanstack/react-query";
import { getMomentTz, getShiftsRangeByParameters } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { Entry } from "../models/Entry";
import { getEntries } from "../repository/LogbookRepository";

const moment = getMomentTz();

export type NavigationMode = "shift" | "period";

type UseLogbookEntriesProps = {
	mode: NavigationMode;
	shiftId: number | null;
	assetId: number | null;
};

type LogbookEntriesFilter = {
	shiftId: number | null;
	assetId: number | null;
};

export type PeriodBounds = { start: Date; end: Date };

export const LOGBOOK_QUERY_KEYS = {
	all: ["logbookEntries"] as const,
	list: (filter: LogbookEntriesFilter) => ["logbookEntries", filter] as const,
};

// Shift mode loads the entries assigned to one shift; period mode loads every entry
// once and the page keeps the ones created inside the period, so browsing is local
export const getLogbookEntriesFilter = ({
	mode,
	shiftId,
	assetId,
}: UseLogbookEntriesProps): LogbookEntriesFilter => ({
	shiftId: mode === "shift" ? shiftId : null,
	assetId,
});

export const useLogbookEntries = (props: UseLogbookEntriesProps) => {
	const filter = getLogbookEntriesFilter(props);

	return useQuery<Entry[], Error>({
		queryKey: LOGBOOK_QUERY_KEYS.list(filter),
		queryFn: ({ signal }) => getEntries({ ...filter, signal }),
		enabled: props.mode === "period" || !!filter.shiftId,
	});
};

// Start of the first shift and end of the last one (UTC, like CreateTimestamp)
export const getPeriodBounds = async (
	startShiftId: number,
	endShiftId: number,
): Promise<PeriodBounds | null> => {
	const resp = await getShiftsRangeByParameters(
		"Day",
		null,
		null,
		startShiftId,
		endShiftId,
	);
	if (!resp.ok) throw new Error(resp.message || "Error fetching period");
	const shifts = get(resp, "data.tables[0].rows", []) as {
		Start: Date | string;
		End: Date | string;
	}[];
	if (!shifts.length) return null;
	return {
		start: moment.utc(shifts[0].Start).toDate(),
		end: moment.utc(shifts[shifts.length - 1].End).toDate(),
	};
};

export const usePeriodBounds = (
	startShiftId: number | null,
	endShiftId: number | null,
) =>
	useQuery<PeriodBounds | null, Error>({
		queryKey: ["logbookPeriodBounds", startShiftId, endShiftId],
		queryFn: () =>
			getPeriodBounds(startShiftId as number, endShiftId as number),
		enabled: !!startShiftId && !!endShiftId,
		staleTime: Number.POSITIVE_INFINITY,
	});

export const isCreatedWithin = (entry: Entry, bounds: PeriodBounds) => {
	const created = entry.CreateTimestamp?.getTime();
	return (
		created != null &&
		created >= bounds.start.getTime() &&
		created < bounds.end.getTime()
	);
};
